// The place secured page (POYT.dc.html, its "Place secured" Tweak; decision 35; docs/data-model.md
// 3.2), in the product's own look. A secured place opens nothing: no menu, no content, no Zoom
// links. The member sees the course's start and session dates, the balance (read from Stripe), the
// date it's due (held per person, so Adam can move it), 1 button to pay it, and what opens when
// it's paid. Paying the balance, or the full price, opens everything at once, through Stripe's
// event. Members never see the word deposit: it's "Your place is secured".
import { useState } from 'react'
import * as f from '../lib/format.js'
import { useNow } from '../lib/clock.js'
import { useMember } from '../app/member.jsx'
import { downloadIcsAll } from '../lib/calendar.js'
import { kindLine } from '../library/model.js'
import { LockIcon } from '../shell/Marks.jsx'
import { ProductFrame } from './ProductFrame.jsx'
import { useSecuredPlace } from './useSecuredPlace.js'
import { payHref, payLabel } from '../lib/balance.js'

const MIN = 60e3

// "Pay the £597+vat balance by 7pm on Tuesday 20 October, the day before Week 1, and everything
// opens at once. Until it's paid, none of it is released."
export function balanceLine({ amount, dueAt, firstSession, nowMs }) {
  const balance = amount ? `the ${amount} balance` : 'the balance'
  let line = `Pay ${balance} and everything opens at once.`
  if (dueAt) {
    const due = Date.parse(dueAt)
    const when = `${f.time(due)} on ${f.longDate(due)}`
    if (due > nowMs) {
      const dayBefore = firstSession && f.daysFrom(due, firstSession.starts_at) === 1 ? `, the day before ${firstSession.title}` : ''
      line = `Pay ${balance} by ${when}${dayBefore}, and everything opens at once.`
    } else {
      line = `Pay ${balance} and everything opens at once. It was due by ${when}.`
    }
  }
  return `${line} Until it's paid, none of it is released.`
}

// The payment link, with their email filled in, so the balance lands on the person whose place it is
// (lib/balance.js, which the Library's Next up and Account share)
export { payHref }

function calendarLabel(count, weeksTotal, firstTitle) {
  if (count >= 2) return weeksTotal && count === weeksTotal ? `Add all ${count} to your calendar` : `Add the ${count} dates to your calendar`
  return `Add ${firstTitle} to your calendar`
}

export default function SecuredPage({ product, owned }) {
  const { me } = useMember()
  const nowMs = useNow()
  const data = useSecuredPlace(product.id)
  const [calAdded, setCalAdded] = useState(false)
  const page = product.settings?.secured_page || {}
  const cohort = owned.cohort
  const ready = data.status === 'ready'

  const dates = ready ? [...data.dates].sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at)) : []
  const firstSession = dates[0] || null
  const upcoming = dates.filter(d => Date.parse(d.starts_at) + (d.duration_minutes || 0) * MIN > nowMs)
  const next = upcoming[0] || null

  const startsOn = cohort?.starts_on ? f.calendarDay(cohort.starts_on) : null
  const startsAhead = startsOn && startsOn.day > f.uk(nowMs).day
  const eyebrow = product.type === 'live_cohort' && cohort ? `Live ${cohort.name.toLowerCase()}` : kindLine(product)

  const dueAt = ready ? data.place?.balance_due_at || null : null
  const amount = ready ? data.amount : null
  const href = ready ? payHref(data.place?.pay_url, me.person?.email) : null
  const ahead = dueAt && Date.parse(dueAt) > nowMs

  function addCal() {
    downloadIcsAll({
      name: product.name,
      events: dates.map((d, i) => ({
        uid: `${product.id}-${i}`, title: `${product.name}, ${d.title}`,
        startMs: Date.parse(d.starts_at), endMs: Date.parse(d.starts_at) + (d.duration_minutes || 60) * MIN,
        path: product.path || '/',
      })),
    })
    setCalAdded(true)
  }

  let countdown = null
  if (next) {
    const days = f.daysFrom(nowMs, next.starts_at)
    countdown = days > 0
      ? { big: String(days), label: `${days === 1 ? 'day' : 'days'} to ${next.title}` }
      : { big: f.time(next.starts_at), label: `${next.title}, today` }
  }

  return (
    <ProductFrame product={product} menu={false}>
      <section className="pp-hero">
        <div className="pp-hero-main">
          <div className="pp-hero-eyebrow">{eyebrow}</div>
          <h1 className="pp-hero-h1">
            {startsAhead
              ? <>Starts {f.weekday(startsOn)} <span className="pp-underline">{f.dayMonthLong(startsOn)}</span></>
              : product.name}
          </h1>
          {page.hero_line && <p className="pp-hero-line">{page.hero_line}</p>}
        </div>
        {countdown && (
          <div className="pp-hero-card">
            <div className="pp-count"><span className="pp-count-big">{countdown.big}</span><span className="pp-count-label">{countdown.label}</span></div>
            <button type="button" className="pp-cal" onClick={addCal}>
              {calAdded ? 'Added to your calendar' : calendarLabel(dates.length, cohort?.weeks_total, dates[0].title)}
            </button>
            {page.join_note && <span className="pp-hero-note">{page.join_note}</span>}
          </div>
        )}
      </section>

      <main className="pp-main">
        <section aria-label="Your place" className={page.locked_items?.length ? 'pp-place' : 'pp-place pp-place--single'}>
          <div className="pp-place-card">
            <span className="pp-place-eyebrow"><LockIcon size={15} />Locked until the balance is paid</span>
            <h2 className="pp-place-h2">Your place is secured</h2>
            {ready && <p className="pp-place-text">{balanceLine({ amount, dueAt, firstSession, nowMs })}</p>}
            {ready && href && <a className="pp-pay" href={href}>{payLabel(amount)}</a>}
            {ready && !href && <p className="pp-place-text">Adam sends the link to pay the balance.</p>}
            {data.status === 'error' && <p className="pp-place-text">Your balance didn't load. Reload the page to try again.</p>}
            {ready && data.questionsEmail && (
              <p className="pp-place-questions">{ahead ? 'Questions before then?' : 'Questions?'} Email {data.questionsEmail}. Adam answers them himself.</p>
            )}
          </div>
          {page.locked_items?.length > 0 && (
            <div className="pp-locked">
              <h3 className="pp-locked-h3">What opens when it's paid</h3>
              {page.locked_items.map(item => (
                <div key={item.title} className="pp-locked-row">
                  <span className="pp-locked-icon"><LockIcon size={16} /></span>
                  <span className="pp-locked-text">
                    <span className="pp-locked-title">{item.title}</span>
                    <span className="pp-locked-note">{item.note}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </ProductFrame>
  )
}
